import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPromotionsDataView } from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { colorStrings, fontFamilyAlt } from "@waytek/Utilities/variables";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    promotionsDataView: getCurrentPromotionsDataView(state),
    settingsCollection: getSettingsCollection(state),
    isClearingCart: state.pages.cart.isClearingCart,
    isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
    isLoading: state.data.carts.isLoading,
});

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasShellContext;

export interface CartLinesWrapperStyles {
    wrapper?: InjectableCss;
    loadingOverlay?: LoadingOverlayProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noCartLinesWrapper?: InjectableCss;
    noCartLinesIconWrapper?: InjectableCss;
    noCartLinesIcon?: IconPresentationProps;
    noCartLinesText?: TypographyProps;
}

export const cartLinesWrapperStyles: CartLinesWrapperStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    noCartLinesWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            height: auto;
            min-height: 400px;
        `,
    },
    noCartLinesIconWrapper: {
        css: css`
            height: 150px;
            width: 150px;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${getColor("common.accent")};
            border-radius: 75px;
            margin-bottom: 20px;
        `,
    },
    noCartLinesIcon: {
        src: ShoppingCart,
        size: 75,
        color: colorStrings.commonGlobalGuardianBlue,
    },
    noCartLinesText: {
        variant: "h4",
        as: "span",
        fontFamily: fontFamilyAlt,
        transform: "none",
    },
};

const StyledSection = getStyledWrapper("section");

const styles = cartLinesWrapperStyles;

const CartLinesWrapper: FC<Props> = ({
    id,
    cart,
    promotionsDataView,
    isClearingCart,
    isUpdatingCartLine,
    isLoading,
    shellContext,
}) => {
    const cartCondition = !cart || (cart.lineCount && !cart.cartLines) || isClearingCart;
    const { isEditing, isInShell } = shellContext;
    if (cartCondition || cart == null) {
        return (
            <StyledSection {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledSection>
        );
    }

    const cartLines = cart.cartLines || [];

    if (cartLines.length === 0) {
        return (
            <StyledWrapper {...styles.noCartLinesWrapper}>
                <StyledWrapper {...styles.noCartLinesIconWrapper}>
                    <Icon {...styles.noCartLinesIcon} />
                </StyledWrapper>
                <Typography {...styles.noCartLinesText} data-test-selector="cart_noCartLinesMessage">
                    {siteMessage("Cart_NoOrderLines")}
                </Typography>

                {isInShell && isEditing && <Zone contentId={id} zoneName="CartLines"></Zone>}
            </StyledWrapper>
        );
    }

    const isLoadingCart = isLoading[cart.id];

    return (
        <StyledSection {...styles.wrapper}>
            <LoadingOverlay
                {...styles.loadingOverlay}
                loading={isLoadingCart || isUpdatingCartLine || !promotionsDataView.value}
            >
                <Zone contentId={id} zoneName="CartLines"></Zone>
            </LoadingOverlay>
        </StyledSection>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(CartLinesWrapper)),
    definition: {
        displayName: "Cart Lines Wrapper",
        group: "Cart",
        allowedContexts: ["CartPage"],
    },
};

export default widgetModule;
